import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { ApiError } from "../api/types";
import { useAuth } from "../store/auth";
import { useLocale } from "../hooks/useLocale";
import styles from "./LoginPage.module.css";

// CreateWorkspacePage serves two audiences:
//   - users with zero workspaces (removed from their last one) — AuthProvider
//     forces them here; there is nowhere else to go, so no back link;
//   - users adding another workspace via the account switcher — they get a
//     back link to /chat.
//
// On success the backend re-mints the session cookie scoped to the new
// workspace; we do a FULL navigation so every store re-hydrates against it.
export function CreateWorkspacePage() {
  const { t, tOr } = useLocale();
  const createWorkspace = useAuth((s) => s.createWorkspace);
  const hasWorkspace = useAuth((s) => s.tenant !== null);

  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [errCode, setErrCode] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setErrCode(null);
    try {
      const redirect = await createWorkspace(name.trim());
      window.location.href = redirect;
    } catch (err) {
      if (err instanceof ApiError) setErrCode(err.code);
      else setErrCode("unknown");
      setBusy(false);
    }
  }

  return (
    <main className={styles.shell}>
      <form className={styles.card} onSubmit={onSubmit}>
        <h1 className={styles.title}>{t("workspace.create_title")}</h1>
        <p className={styles.subtitle}>
          {hasWorkspace
            ? t("workspace.create_subtitle_extra")
            : t("workspace.create_subtitle_first")}
        </p>
        <label className={styles.label}>
          <span>{t("workspace.create_name")}</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={255}
            autoFocus
            required
          />
        </label>
        {errCode && (
          <p className={styles.error}>
            {tOr(`workspace.error.${errCode}`, "workspace.error.unknown")}
          </p>
        )}
        <button type="submit" className={styles.submit} disabled={busy}>
          {busy ? t("common.loading") : t("workspace.create_submit")}
        </button>
        {hasWorkspace && (
          <div className={styles.links}>
            <Link to="/chat">{t("workspace.create_back")}</Link>
          </div>
        )}
      </form>
    </main>
  );
}
